//** Import Components */
import { useState, useEffect } from "react";
import {
  Table,
  Tooltip,
  Empty,
  Space,
  Tag,
  Form,
  Modal,
  Button,
  Select,
  message,
  Collapse,
  Pagination,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { TABLE_DATE_FORMAT } from "../common/constants";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import axiosWithAuth from "../utils/axiosWithAuth";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faReceipt, faRotateRight } from "@fortawesome/free-solid-svg-icons";

const { Panel } = Collapse;
const { Option } = Select;

export const useManagementOrders = (getAllOrders) => {
  const dateFormat = TABLE_DATE_FORMAT;
  let navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [modalForm] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusValue, setStatusValue] = useState({ status: "" });
  const [orderID, setOrderID] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(7);

  const showModal = () => setOpenModal(true);
  const closeModal = () => setOpenModal(false);

  const submitModalForm = () => {
    setIsSubmitting(true);
    modalForm.submit();
    closeModal();
  };

  const onFinish = (value) => {
    const APIURL = `/api/restaurant/order/${orderID}`;

    const listingData = {
      order_status: value.status,
    };

    axiosWithAuth()
      .put(APIURL, listingData)
      .then(() => {
        message.success({
          content: `Status edited!`,
          style: { background: "none" },
        });
        closeModal();
        setIsSubmitting(false);
        modalForm.resetFields();
        getAllOrders();
      })
      .catch((err) => {
        message.error(err.message);
        setIsSubmitting(false);
      });
  };

  const onFinishFailed = (errorInfo) => {
    setIsSubmitting(false);
  };

  const columns = [
    {
      title: "Order ID",
      key: "order_id",
      render: (text) => <>{text.id}</>,
    },
    {
      title: "Date Created",
      key: "created",
      render: (text) => (
        <>{moment(new Date(text && text.date_order)).format(dateFormat)}</>
      ),
    },
    {
      title: "Delivery Type",
      key: "delivery_type",
      render: (text) => <>{text.delivery_type}</>,
    },
    {
      title: "Status",
      key: "staus",
      render: (text) => <>{text.order_status}</>,
    },
    {
      title: "Total Amount",
      key: "total_amount",
      render: (text) => <>$ {text.total_price_order}</>,
    },
    {
      title: "Action",
      key: "action",
      render: (text) => (
        <Space size="middle">
          <button
            onClick={() => {
              navigate(`${text.id}`);
            }}
          >
            <Tooltip title="Receipt">
              <FontAwesomeIcon icon={faReceipt} />
            </Tooltip>
          </button>
          <button
            onClick={() => {
              setOrderID(text.id);
              setStatusValue({ status: text.order_status });
              showModal();
            }}
          >
            <Tooltip title="Modify">
              <FontAwesomeIcon icon={faRotateRight} />
            </Tooltip>
          </button>
        </Space>
      ),
    },
  ];

  const ListComponent = ({ emptyMessage, listItems }) => {
    const isMobile = window.innerWidth <= 768;
    const paginatedData = listItems.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize,
    );

    return (
      <>
        {listItems.length > 0 ? (
          isMobile ? (
            <>
              <Collapse>
                {paginatedData.map((item) => (
                  <Panel
                    header={`Order ID: ${item.id} - ${moment(
                      new Date(item.date_order),
                    ).format(dateFormat)}`}
                    key={item.id}
                  >
                    <p>
                      <strong>Delivery Type:</strong> {item.delivery_type}
                    </p>
                    <p>
                      <strong>Status:</strong> {item.order_status}
                    </p>
                    <p>
                      <strong>Total Amount:</strong> ${item.total_price_order}
                    </p>
                    <Space size="middle">
                      <Button
                        type="primary"
                        onClick={() => navigate(`${item.id}`)}
                      >
                        <Tooltip title="Receipt">
                          <FontAwesomeIcon icon={faReceipt} />
                        </Tooltip>{" "}
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => {
                          setOrderID(item.id);
                          setStatusValue({ status: item.order_status });
                          showModal();
                        }}
                      >
                        <Tooltip title="Modify">
                          <FontAwesomeIcon icon={faRotateRight} />
                        </Tooltip>
                      </Button>
                    </Space>
                  </Panel>
                ))}
              </Collapse>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={listItems.length}
                onChange={(page) => setCurrentPage(page)}
                style={{ marginTop: "1rem", textAlign: "center" }}
              />
            </>
          ) : (
            <Table
              columns={columns}
              dataSource={listItems}
              pagination={{ pageSize }}
            />
          )
        ) : (
          <Empty
            image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
            imageStyle={{ height: 60 }}
            description={<span>{emptyMessage}</span>}
          />
        )}

        <Modal
          open={openModal}
          title="Select Status"
          onCancel={closeModal}
          footer={[
            <Button key="back" onClick={closeModal}>
              Close
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={isSubmitting}
              onClick={submitModalForm}
            >
              Submit
            </Button>,
          ]}
        >
          <Form
            form={modalForm}
            layout="vertical"
            name="userForm"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Select a option"
                defaultValue={statusValue.status}
              >
                <Option value="Processing">Processing</Option>
                <Option value="Ready">Ready</Option>
                <Option value="Shipped">On the way</Option>
                <Option value="Completed">Completed</Option>
                <Option value="Canceled">Canceled</Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </>
    );
  };

  return [ListComponent];
};
